/*
 * Geometría de la "barra de jornada" (components/BarraJornada.js): entrada, almuerzo y salida de una
 * persona dibujados sobre el día, con la línea de la hora oficial (08:00) y la franja de tolerancia
 * (hasta 08:10) como referencia común a todas las barras.
 *
 * El eje va de TIMELINE_START (06:00) a TIMELINE_END (20:00): es la franja del kiosco (entrada desde
 * 06:00, salida hasta 20:00, migración 0004); una marca fuera del eje se pega al borde.
 * Posiciones y anchos en porcentaje del eje, con 2 decimales. Funciones puras salvo currentMinutes().
 */
import {hhmm, toMinutes} from './rows';

export const TIMELINE_START = 6 * 60;
export const TIMELINE_END = 20 * 60;
export const OFFICIAL_MINUTES = 8 * 60;   // hora oficial de entrada (spec 4.2)
export const TOLERANCE_MINUTES = 10;      // tolerancia (spec 4.2)
export const TIMELINE_TICKS = [6, 8, 10, 12, 14, 16, 18, 20];

const round2 = (value) => Math.round(value * 100) / 100;

/** Minutos desde la medianoche -> % del eje (acotado a 0-100). */
export function position(minutes) {
    const clamped = Math.min(Math.max(minutes, TIMELINE_START), TIMELINE_END);
    return round2(((clamped - TIMELINE_START) / (TIMELINE_END - TIMELINE_START)) * 100);
}

function segment(kind, from, to) {
    const left = position(from);
    return {kind, left, width: round2(position(to) - left)};
}

/**
 * Tramos de la jornada: 'trabajo', 'almuerzo' y 'en_curso' (trabajo de hoy aún sin salida, hasta
 * `nowMinutes`). Sin entrada no hay tramos. Una jornada pasada sin salida solo muestra la entrada.
 * @param row fila de /attendance/jornadas/
 * @param nowMinutes minutos de la hora actual si la fila es de hoy; null en otro caso
 */
export function timelineSegments(row, nowMinutes = null) {
    const entry = toMinutes(row.entrada);
    if (entry === null) return [];
    const exit = toMinutes(row.salida);
    const ongoing = exit === null && row.estado === 'en_curso' && nowMinutes !== null;
    const end = exit !== null ? exit : (ongoing ? Math.max(nowMinutes, entry) : null);
    if (end === null) return [];
    const workKind = ongoing ? 'en_curso' : 'trabajo';
    const lunchStart = toMinutes(row.inicio_almuerzo);
    const lunchEnd = toMinutes(row.fin_almuerzo);
    let parts;
    if (lunchStart !== null && lunchStart >= entry && lunchStart <= end) {
        const back = lunchEnd !== null && lunchEnd >= lunchStart && lunchEnd <= end ? lunchEnd : null;
        parts = back === null
            ? [segment('trabajo', entry, lunchStart), segment('almuerzo', lunchStart, end)]
            : [segment('trabajo', entry, lunchStart), segment('almuerzo', lunchStart, back), segment(workKind, back, end)];
    } else {
        parts = [segment(workKind, entry, end)];
    }
    return parts.filter((part) => part.width > 0);
}

/** Texto para lectores de pantalla (la barra es role="img"). */
export function timelineLabel(row) {
    if (!row.entrada) return 'Sin marca de entrada';
    const parts = [`Entrada ${hhmm(row.entrada)}`];
    if (row.inicio_almuerzo || row.fin_almuerzo) parts.push(`almuerzo ${hhmm(row.inicio_almuerzo)} a ${hhmm(row.fin_almuerzo)}`);
    parts.push(row.salida ? `salida ${hhmm(row.salida)}` : 'sin salida');
    return parts.join(', ');
}

/** Minutos de la hora local actual (lo único que lee el reloj). */
export function currentMinutes(now = new Date()) {
    return now.getHours() * 60 + now.getMinutes();
}
